import useSWR, { mutate } from 'swr';
import api from '../../services/api';

export interface CustomField {
  id: number;
  entityType: string;
  fieldName: string;
  fieldLabel: string;
  fieldType: 'text' | 'number' | 'date' | 'select' | 'checkbox' | 'textarea';
  options?: any[];
  isRequired: boolean;
  validation?: any;
  order: number;
  value?: any;
  _count?: {
    values: number;
  };
}

export interface CustomFieldValue {
  id: number;
  customFieldId: number;
  entityType: string;
  entityId: number;
  value: any;
  customField?: CustomField;
}

// Fetch all custom fields (for forms / dynamic field renderer)
export const useCustomFields = (entityType?: string) => {
  const url = entityType ? `/custom-fields?entityType=${entityType}` : '/custom-fields';
  const { data, error, isLoading, mutate } = useSWR<CustomField[]>(
    url,
    async (url: string) => {
      const response = await api.get(url);
      return response.data;
    },
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data: data || [],
    isLoading,
    error,
    mutate,
  };
};

// Fetch all custom fields for the management list page
export const useCustomFieldsForManagement = (entityType?: string) => {
  const url = entityType
    ? `/custom-fields/manage?entityType=${entityType}`
    : '/custom-fields/manage';
  const { data, error, isLoading, mutate } = useSWR<CustomField[]>(
    url,
    async (url: string) => {
      const response = await api.get(url);
      return response.data;
    },
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data: data || [],
    isLoading,
    error,
    mutate,
  };
};

// Fetch a single custom field
export const useCustomField = (id: number | null) => {
  const { data, error, isLoading, mutate } = useSWR<CustomField>(
    id ? `/custom-fields/${id}` : null,
    async (url: string) => {
      const response = await api.get(url);
      return response.data;
    },
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data,
    isLoading,
    error,
    mutate,
  };
};

// Fetch custom field values for an entity
export const useCustomFieldValues = (entityType: string, entityId: number | null) => {
  const { data, error, isLoading, mutate } = useSWR<CustomField[]>(
    entityType && entityId ? `/custom-fields/values/${entityType}/${entityId}` : null,
    async (url: string) => {
      const response = await api.get(url);
      return response.data;
    },
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
    }
  );

  return {
    data: data || [],
    isLoading,
    error,
    mutate,
  };
};

// Create a custom field
export const createCustomField = async (fieldData: Partial<CustomField>): Promise<CustomField> => {
  const response = await api.post('/custom-fields', fieldData);
  await mutate('/custom-fields');
  await mutate('/custom-fields/manage');
  return response.data;
};

// Update a custom field
export const updateCustomField = async (id: number, fieldData: Partial<CustomField>): Promise<CustomField> => {
  const response = await api.patch(`/custom-fields/${id}`, fieldData);
  await mutate(`/custom-fields/${id}`);
  await mutate('/custom-fields');
  await mutate('/custom-fields/manage');
  return response.data;
};

// Delete a custom field
export const deleteCustomField = async (id: number): Promise<void> => {
  await api.delete(`/custom-fields/${id}`);
  await mutate('/custom-fields');
  await mutate('/custom-fields/manage');
};

// Set custom field values for an entity
export const setCustomFieldValues = async (
  entityType: string,
  entityId: number,
  values: Array<{ customFieldId: number; value: any }>
): Promise<any> => {
  const response = await api.post(`/custom-fields/values/${entityType}/${entityId}`, values);
  await mutate(`/custom-fields/values/${entityType}/${entityId}`);
  return response.data;
};

// Set a single custom field value
export const setCustomFieldValue = async (
  entityType: string,
  entityId: number,
  customFieldId: number,
  value: any
): Promise<any> => {
  const response = await api.post(`/custom-fields/values/${entityType}/${entityId}/${customFieldId}`, { value });
  await mutate(`/custom-fields/values/${entityType}/${entityId}`);
  return response.data;
};

// Delete a custom field value
export const deleteCustomFieldValue = async (
  entityType: string,
  entityId: number,
  customFieldId: number
): Promise<void> => {
  await api.delete(`/custom-fields/values/${entityType}/${entityId}/${customFieldId}`);
  await mutate(`/custom-fields/values/${entityType}/${entityId}`);
};
